import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { runnerApi, type InboxCounts } from './runnerApiClient';

export const INBOX_UPDATED_EVENT = 'runner-inbox-updated';

export function emitInboxUpdated(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(INBOX_UPDATED_EVENT));
}

/** Push payloads name an app screen; these are the portal pages that match. */
const SCREEN_ROUTES: Record<string, { to: string; navKey: string }> = {
  today: { to: '/runs', navKey: 'nav.runs' },
  runs: { to: '/runs', navKey: 'nav.runs' },
  run: { to: '/runs', navKey: 'nav.runs' },
  disputes: { to: '/disputes', navKey: 'runnerNav.disputes' },
  customers: { to: '/potential-customers', navKey: 'runnerHome.potentialTitle' },
  limits: { to: '/schedule', navKey: 'nav.schedule' },
  insurance: { to: '/insurance', navKey: 'nav.insurance' },
  earnings: { to: '/earnings', navKey: 'nav.earnings' },
  declinedPrescreen: { to: '/application', navKey: 'nav.application' },
};

export function routeForScreen(screen: unknown, runId?: unknown): { to: string; navKey: string } | null {
  const target = typeof screen === 'string' ? SCREEN_ROUTES[screen] ?? null : null;
  if (target?.to === '/runs' && typeof runId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(runId)) return { ...target, to: `/runs?run=${runId}` };
  return target;
}

export function badgeLabel(n: number): string {
  return n > 99 ? '99+' : String(n);
}

const ZERO = { unreadTotal: 0, alertUnread: 0 };

/** Header badge counts: refreshed on every navigation, on window focus, and after inbox actions. */
export function useInboxCounts(enabled: boolean): { unreadTotal: number; alertUnread: number } {
  const { pathname, search } = useLocation();
  const [counts, setCounts] = useState<Pick<InboxCounts, 'unreadTotal' | 'alertUnread'>>(ZERO);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const c = await runnerApi.messageCounts();
      setCounts({ unreadTotal: c.unreadTotal, alertUnread: c.alertUnread });
    } catch {
      /* badges keep their last value */
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh, pathname, search]);

  useEffect(() => {
    if (!enabled) return;
    const onChange = () => void refresh();
    window.addEventListener('focus', onChange);
    window.addEventListener(INBOX_UPDATED_EVENT, onChange);
    return () => {
      window.removeEventListener('focus', onChange);
      window.removeEventListener(INBOX_UPDATED_EVENT, onChange);
    };
  }, [enabled, refresh]);

  return enabled ? counts : ZERO;
}
